import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BookOpen,
  Clock3,
  Copy,
  Download,
  FileText,
  Filter,
  Grid3x3,
  Link2,
  List,
  Search,
  ShieldCheck,
  Tag,
  Video as VideoIcon,
  Library,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FioriPageHeader } from "@/components/fiori";
import PDFViewer from "@/components/PDFViewer";
import VideoPlayer from "@/components/VideoPlayer";
import { trpc } from "@/utils/trpc";
import { toast } from "sonner";

type ViewMode = "grid" | "list";
type SortMode = "newest" | "oldest" | "title";

function parseTags(tags: unknown): string[] {
  if (Array.isArray(tags)) {
    return tags.map((tag) => String(tag).trim()).filter(Boolean);
  }
  if (typeof tags === "string") {
    return tags.split(",").map((tag) => tag.trim()).filter(Boolean);
  }
  return [];
}

function getContentUrl(item: any) {
  return item.fileUrl || item.url || "";
}

function getContentTypeLabel(item: any, t: (key: string, fallback: string) => string) {
  return item.contentType === "PDF" ? t("knowledge.pdf", "PDF") : t("knowledge.video", "Video");
}

function getPublishedDate(item: any) {
  return item.publishedAt || item.updatedAt || item.createdAt || null;
}

function formatDate(dateValue: string | Date | null | undefined) {
  if (!dateValue) return "—";
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function getRelativeDate(dateValue: string | Date | null | undefined, t: (key: string, opts?: any) => string) {
  if (!dateValue) return t("knowledge.noPublishDate", "No publish date");
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return t("knowledge.noPublishDate", "No publish date");

  const diffMs = Date.now() - parsed.getTime();
  const diffDays = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));

  if (diffDays === 0) return t("knowledge.today");
  if (diffDays === 1) return t("knowledge.oneDayAgo");
  if (diffDays < 7) return `${diffDays} ${t("knowledge.daysAgoSuffix")}`;
  const weeks = Math.floor(diffDays / 7);
  if (diffDays < 30) return `${weeks} ${t("knowledge.weeksAgoSuffix")}`;
  const months = Math.floor(diffDays / 30);
  return `${months} ${t("knowledge.monthsAgoSuffix")}`;
}

function ContentCard({
  item,
  isAcknowledged,
  onOpen,
  onShare,
  t,
}: {
  item: any;
  isAcknowledged: boolean;
  onOpen: () => void;
  onShare: () => void;
  t: (key: string, fallback?: any, opts?: any) => string;
}) {
  const contentUrl = getContentUrl(item);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group rounded-2xl border border-border bg-background p-5 text-start shadow-sm transition hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-md w-full"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <div className="rounded-lg bg-[#E8DCF5] p-2">
            {item.contentType === "PDF" ? (
              <FileText className="h-5 w-5 text-[#5B2C93]" />
            ) : (
              <VideoIcon className="h-5 w-5 text-[#5B2C93]" />
            )}
          </div>
          <div className="min-w-0">
            <h3 className="line-clamp-2 text-base font-semibold">{item.title}</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              {getRelativeDate(getPublishedDate(item), t)}
            </p>
          </div>
        </div>
        {item.mandatoryAck && (
          <Badge className="bg-[#FEF3C7] text-[#D97706] hover:bg-[#FEF3C7] shrink-0 rounded font-medium">
            {t("knowledge.required", "Required")}
          </Badge>
        )}
      </div>

      <p className="mt-4 line-clamp-3 text-sm text-[#6B6B6B]">
        {item.description || t("knowledge.noDescription", "No description provided for this content yet.")}
      </p>

      <div className="mt-4 flex flex-wrap gap-2">
        <Badge className="border-[#E0E0E0] text-[#6B6B6B] bg-white rounded">{item.category}</Badge>
        <Badge className="bg-[#F5F5F5] text-[#6B6B6B] rounded">{getContentTypeLabel(item, t)}</Badge>
        {parseTags(item.tags).slice(0, 2).map((tag) => (
          <Badge key={tag} variant="outline" className="bg-muted/30">
            {tag}
          </Badge>
        ))}
      </div>

      <div className="mt-5 flex items-center justify-between gap-3 border-t border-[#E0E0E0] pt-4">
        <span
          className={`text-xs font-medium ${
            isAcknowledged
              ? "text-[#059669]"
              : item.mandatoryAck
                ? "text-[#D97706]"
                : "text-[#9CA3AF]"
          }`}
        >
          {isAcknowledged ? t("knowledge.acknowledged", "Acknowledged") : ""}
        </span>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            title={t("knowledge.copyLink", "Copy shareable link")}
            className="text-[#6B6B6B] hover:text-[#5B2C93] hover:bg-[#E8DCF5]"
            onClick={(e) => {
              e.stopPropagation();
              onShare();
            }}
          >
            <Link2 className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            title={t("common.download", "Download")}
            className="text-[#6B6B6B] hover:text-[#5B2C93] hover:bg-[#E8DCF5]"
            onClick={(e) => {
              e.stopPropagation();
              if (contentUrl) window.open(contentUrl, "_blank", "noopener,noreferrer");
            }}
            disabled={!contentUrl}
          >
            <Download className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </button>
  );
}

function ContentListRow({
  item,
  isAcknowledged,
  onOpen,
  onShare,
  t,
}: {
  item: any;
  isAcknowledged: boolean;
  onOpen: () => void;
  onShare: () => void;
  t: (key: string, fallback?: any, opts?: any) => string;
}) {
  const contentUrl = getContentUrl(item);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-center justify-between gap-4 rounded-2xl border border-border bg-background p-4 text-start transition hover:border-primary/30 hover:bg-accent/30"
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          {item.contentType === "PDF" ? (
            <FileText className="h-4 w-4 text-[#5B2C93]" />
          ) : (
            <VideoIcon className="h-4 w-4 text-[#5B2C93]" />
          )}
          <h3 className="text-sm font-semibold text-[#2C2C2C]">{item.title}</h3>
          <Badge className="border-[#E0E0E0] text-[#6B6B6B] bg-white rounded">{item.category}</Badge>
          {item.mandatoryAck && (
            <Badge className="bg-[#FEF3C7] text-[#D97706] hover:bg-[#FEF3C7] rounded font-medium">
              {t("knowledge.required", "Required")}
            </Badge>
          )}
        </div>
        <p className="mt-2 line-clamp-2 text-sm text-[#6B6B6B]">
          {item.description || t("knowledge.noDescription", "No description provided for this content yet.")}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-[#9CA3AF]">
          <span>{getRelativeDate(getPublishedDate(item), t)}</span>
          <span>{getContentTypeLabel(item, t)}</span>
          {isAcknowledged && (
            <span className="text-[#059669]">{t("knowledge.acknowledged", "Acknowledged")}</span>
          )}
        </div>
      </div>

      <div className="flex gap-2 shrink-0">
        <Button
          variant="ghost"
          size="sm"
          title={t("knowledge.copyLink", "Copy shareable link")}
          className="text-[#6B6B6B] hover:text-[#5B2C93] hover:bg-[#E8DCF5]"
          onClick={(e) => {
            e.stopPropagation();
            onShare();
          }}
        >
          <Link2 className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          title={t("common.download", "Download")}
          className="text-[#6B6B6B] hover:text-[#5B2C93] hover:bg-[#E8DCF5]"
          onClick={(e) => {
            e.stopPropagation();
            if (contentUrl) window.open(contentUrl, "_blank", "noopener,noreferrer");
          }}
          disabled={!contentUrl}
        >
          <Download className="h-4 w-4" />
        </Button>
      </div>
    </button>
  );
}

export default function KnowledgeCenter() {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState("All");
  const [selectedTag, setSelectedTag] = useState("all");
  const [sortBy, setSortBy] = useState<SortMode>("newest");
  const [viewerItem, setViewerItem] = useState<any | null>(null);
  const [activeCategory, setActiveCategory] = useState("all");

  const { data: contentList = [], isLoading, error } = trpc.knowledge.getLibrary.useQuery();
  const { data: acknowledgementRows = [] } = trpc.knowledge.getUserAcknowledgements.useQuery();

  // Deep-link: if URL contains ?doc={id}, open that document once the library loads
  useEffect(() => {
    if (isLoading || contentList.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const docId = params.get("doc");
    if (!docId) return;
    const target = (contentList as any[]).find((item: any) => String(item.id) === docId);
    if (target) {
      setViewerItem(target);
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, "", cleanUrl);
    }
  }, [isLoading, contentList]);

  const acknowledgedIds = useMemo(() => {
    const fromApi = acknowledgementRows.map((row: any) => row.contentId);
    return new Set<number>(fromApi);
  }, [acknowledgementRows]);

  const populatedCategories = useMemo(() => {
    const cats = new Set<string>();
    (contentList as any[]).forEach((item: any) => { if (item.category) cats.add(item.category); });
    return Array.from(cats).sort((a, b) => a.localeCompare(b));
  }, [contentList]);

  const availableTags = useMemo(() => {
    const tags = new Set<string>();
    (contentList as any[]).forEach((item: any) => {
      parseTags(item.tags).forEach((tag) => tags.add(tag));
    });
    return Array.from(tags).sort((a, b) => a.localeCompare(b));
  }, [contentList]);

  const filteredContent = useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();

    const items = (contentList as any[]).filter((item: any) => {
      const description = (item.description || "").toLowerCase();
      const title = (item.title || "").toLowerCase();
      const tags = parseTags(item.tags);
      const matchesSearch =
        !normalizedSearch ||
        title.includes(normalizedSearch) ||
        description.includes(normalizedSearch) ||
        tags.some((tag) => tag.toLowerCase().includes(normalizedSearch));

      const matchesCategory = activeCategory === "all" || item.category === activeCategory;
      const matchesType =
        selectedType === "All" ||
        (selectedType === "PDF" && item.contentType === "PDF") ||
        (selectedType === "Video" && item.contentType !== "PDF");
      const matchesTag = selectedTag === "all" || tags.includes(selectedTag);

      return matchesSearch && matchesCategory && matchesType && matchesTag;
    });

    items.sort((a: any, b: any) => {
      const dateA = new Date(getPublishedDate(a) || 0).getTime();
      const dateB = new Date(getPublishedDate(b) || 0).getTime();

      switch (sortBy) {
        case "oldest":
          return dateA - dateB;
        case "title":
          return a.title.localeCompare(b.title);
        case "newest":
        default:
          return dateB - dateA;
      }
    });

    return items;
  }, [contentList, searchQuery, activeCategory, selectedType, selectedTag, sortBy]);

  const handleShare = (item: any) => {
    const shareUrl = `${window.location.origin}/knowledge-center?doc=${item.id}`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      toast.success(t("knowledge.linkCopied", "Link copied to clipboard"), { description: item.title });
    }).catch(() => {
      toast.error(t("knowledge.linkCopyFailed", "Could not copy link"));
    });
  };

  const contentUrl = viewerItem ? getContentUrl(viewerItem) : "";

  return (
    <div className="-mx-4 md:-mx-6 -mb-4 md:-mb-6 min-h-[calc(100vh-4rem)] bg-white px-4 md:px-6">
      <FioriPageHeader
        title={t("knowledge.title", "Knowledge Center")}
        subtitle={t("knowledge.subtitle", "Policies, procedures, training, and operational guidance for Centre3")}
        icon={<Library className="h-5 w-5" />}
      />

      <div className="px-0 pb-6">
        <div className="bg-white rounded-lg border border-[#E0E0E0]">
          <div className="p-4 border-b border-[#F0F0F0]">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-[#2C2C2C]">{t("knowledge.browseContent", "Browse Knowledge Content")}</h2>
                <p className="text-sm text-[#6B6B6B]">{t("knowledge.browseSubtitle", "Search, filter, and explore the full knowledge library")}</p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant={viewMode === "grid" ? "default" : "outline"}
                  size="icon"
                  onClick={() => setViewMode("grid")}
                  className={viewMode === "grid" ? "bg-[#5B2C93] text-white hover:bg-[#4A2380]" : "border-[#E0E0E0] text-[#6B6B6B]"}
                >
                  <Grid3x3 className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === "list" ? "default" : "outline"}
                  size="icon"
                  onClick={() => setViewMode("list")}
                  className={viewMode === "list" ? "bg-[#5B2C93] text-white hover:bg-[#4A2380]" : "border-[#E0E0E0] text-[#6B6B6B]"}
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Search & Filters */}
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4 mt-4">
              <div className="xl:col-span-2">
                <label className="text-xs font-medium text-[#6B6B6B] uppercase tracking-wide">{t("knowledge.search", "Search")}</label>
                <div className="relative mt-1">
                  <Search className="absolute start-3 top-3 h-4 w-4 text-[#9CA3AF]" />
                  <Input
                    placeholder={t("knowledge.searchPlaceholder", "Search by title, description, or tags")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="ps-10 border-[#E0E0E0] focus:ring-[#5B2C93]"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-[#6B6B6B] uppercase tracking-wide">{t("knowledge.type", "Type")}</label>
                <Select value={selectedType} onValueChange={setSelectedType}>
                  <SelectTrigger className="mt-1 border-[#E0E0E0] focus:ring-[#5B2C93]">
                    <SelectValue placeholder={t("knowledge.allTypes", "All types")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All">{t("knowledge.allTypes", "All types")}</SelectItem>
                    <SelectItem value="PDF">{t("knowledge.pdf", "PDF")}</SelectItem>
                    <SelectItem value="Video">{t("knowledge.video", "Video")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-[#6B6B6B] uppercase tracking-wide">{t("knowledge.sort", "Sort")}</label>
                <Select value={sortBy} onValueChange={(value) => setSortBy(value as SortMode)}>
                  <SelectTrigger className="mt-1 border-[#E0E0E0] focus:ring-[#5B2C93]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">{t("knowledge.newestFirst", "Newest first")}</SelectItem>
                    <SelectItem value="oldest">{t("knowledge.oldestFirst", "Oldest first")}</SelectItem>
                    <SelectItem value="title">{t("knowledge.titleAZ", "Title A-Z")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Tag filters */}
            {availableTags.length > 0 && (
              <div className="mt-4">
                <div className="mb-2 flex items-center gap-2 text-xs font-medium text-[#6B6B6B] uppercase tracking-wide">
                  <Filter className="h-4 w-4 text-[#9CA3AF]" />
                  {t("knowledge.topicTags", "Topic tags")}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant={selectedTag === "all" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedTag("all")}
                    className={selectedTag === "all" ? "bg-[#5B2C93] text-white hover:bg-[#4A2380]" : "border-[#E0E0E0] text-[#6B6B6B] hover:border-[#5B2C93]"}
                  >
                    {t("knowledge.allTags", "All tags")}
                  </Button>
                  {availableTags.slice(0, 12).map((tag) => (
                    <Button
                      key={tag}
                      variant={selectedTag === tag ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedTag(tag)}
                      className={selectedTag === tag ? "bg-[#5B2C93] text-white hover:bg-[#4A2380]" : "border-[#E0E0E0] text-[#6B6B6B] hover:border-[#5B2C93]"}
                    >
                      <Tag className="me-1 h-3.5 w-3.5" />
                      {tag}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="p-4">
            {isLoading && (
              <div className="flex items-center justify-center py-16">
                <div className="text-center">
                  <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-b-2 border-[#5B2C93]" />
                  <p className="text-sm text-[#6B6B6B]">{t("knowledge.loading", "Loading knowledge content...")}</p>
                </div>
              </div>
            )}

            {error && (
              <div className="rounded-lg border border-[#FF6B6B]/30 bg-[#FFE5E5] p-4 text-sm text-[#FF6B6B]">
                {t("knowledge.loadError", "Failed to load content. Please try again.")}
              </div>
            )}

            {!isLoading && !error && (
              <Tabs value={activeCategory} onValueChange={setActiveCategory}>
                {/* Category Pills */}
                <div className="mb-4 overflow-x-auto pb-1">
                  <div className="flex gap-2 w-max">
                    <button
                      type="button"
                      onClick={() => setActiveCategory("all")}
                      className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${
                        activeCategory === "all"
                          ? "bg-[#5B2C93] text-white"
                          : "bg-[#F5F5F5] text-[#6B6B6B] hover:bg-[#E8DCF5] hover:text-[#5B2C93]"
                      }`}
                    >
                      {t("common.all", "All")}
                      <span className={`inline-flex items-center justify-center rounded-full px-1.5 py-0.5 text-xs font-medium ${
                        activeCategory === "all" ? "bg-white/20 text-white" : "bg-[#E0E0E0] text-[#6B6B6B]"
                      }`}>{(contentList as any[]).length}</span>
                    </button>
                    {populatedCategories.map((cat) => {
                      const count = (contentList as any[]).filter((item: any) => item.category === cat).length;
                      const isActive = activeCategory === cat;
                      return (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => setActiveCategory(cat)}
                          className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${
                            isActive
                              ? "bg-[#5B2C93] text-white"
                              : "bg-[#F5F5F5] text-[#6B6B6B] hover:bg-[#E8DCF5] hover:text-[#5B2C93]"
                          }`}
                        >
                          {cat}
                          <span className={`inline-flex items-center justify-center rounded-full px-1.5 py-0.5 text-xs font-medium ${
                            isActive ? "bg-white/20 text-white" : "bg-[#E0E0E0] text-[#6B6B6B]"
                          }`}>{count}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Results summary */}
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#E0E0E0] bg-[#FAFAFA] px-4 py-3 text-sm mb-4">
                  <div className="flex flex-wrap items-center gap-2 text-[#6B6B6B]">
                    <span>{t("knowledge.results", "{{count}} result(s)", { count: filteredContent.length })}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[#6B6B6B] hover:text-[#5B2C93] hover:bg-[#E8DCF5]"
                    onClick={() => {
                      setSearchQuery("");
                      setSelectedType("All");
                      setSelectedTag("all");
                      setSortBy("newest");
                      setActiveCategory("all");
                    }}
                  >
                    {t("knowledge.resetFilters", "Reset filters")}
                  </Button>
                </div>

                {/* Content for "all" tab and each category tab */}
                {["all", ...populatedCategories].map((cat) => (
                  <TabsContent key={cat} value={cat} className="mt-0">
                    {viewMode === "grid" ? (
                      <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
                        {filteredContent.map((item: any) => (
                          <ContentCard
                            key={item.id}
                            item={item}
                            isAcknowledged={acknowledgedIds.has(item.id)}
                            onOpen={() => setViewerItem(item)}
                            onShare={() => handleShare(item)}
                            t={t as any}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {filteredContent.map((item: any) => (
                          <ContentListRow
                            key={item.id}
                            item={item}
                            isAcknowledged={acknowledgedIds.has(item.id)}
                            onOpen={() => setViewerItem(item)}
                            onShare={() => handleShare(item)}
                            t={t as any}
                          />
                        ))}
                      </div>
                    )}

                    {filteredContent.length === 0 && (
                      <div className="rounded-2xl border border-dashed border-[#E0E0E0] p-12 text-center">
                        <BookOpen className="mx-auto h-8 w-8 text-[#9CA3AF]" />
                        <p className="mt-4 text-base font-medium text-[#2C2C2C]">{t("knowledge.noResults", "No matching documents found")}</p>
                        <p className="mt-1 text-sm text-[#6B6B6B]">
                          {t("knowledge.noResultsHint", "Adjust the search terms or clear the active filters to broaden the results.")}
                        </p>
                      </div>
                    )}
                  </TabsContent>
                ))}
              </Tabs>
            )}
          </div>
        </div>
      </div>

      {/* PDF/Video Viewer Modal */}
      <Dialog open={!!viewerItem} onOpenChange={(open) => { if (!open) setViewerItem(null); }}>
        <DialogContent className="max-w-5xl w-full p-0 gap-0 overflow-hidden flex flex-col" style={{ height: "92vh", maxHeight: "92vh" }}>
          {viewerItem && (
            <>
              <DialogHeader className="px-6 pt-5 pb-3 border-b border-[#E0E0E0]">
                <div className="flex items-start justify-between gap-4 pe-8">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <Badge className="border-[#E0E0E0] text-[#6B6B6B] bg-white rounded">{viewerItem.category}</Badge>
                      <Badge className="bg-[#F5F5F5] text-[#6B6B6B] rounded">{getContentTypeLabel(viewerItem, t as any)}</Badge>
                      {viewerItem.mandatoryAck && (
                        <Badge className="bg-[#FEF3C7] text-[#D97706] hover:bg-[#FEF3C7] rounded font-medium">
                          {t("knowledge.mandatory", "Mandatory")}
                        </Badge>
                      )}
                    </div>
                    <DialogTitle className="text-xl font-semibold leading-tight text-[#2C2C2C]">{viewerItem.title}</DialogTitle>
                    {viewerItem.description && (
                      <p className="mt-1 text-sm text-[#6B6B6B] line-clamp-2">{viewerItem.description}</p>
                    )}
                    <div className="flex flex-wrap gap-4 mt-2 text-xs text-[#9CA3AF]">
                      <span className="flex items-center gap-1">
                        <Clock3 className="h-3.5 w-3.5" />
                        {t("knowledge.published", "Published")} {formatDate(getPublishedDate(viewerItem))}
                      </span>
                      {acknowledgedIds.has(viewerItem.id) && (
                        <span className="flex items-center gap-1 text-[#059669]">
                          <ShieldCheck className="h-3.5 w-3.5" />
                          {t("knowledge.acknowledged", "Acknowledged")}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5 border-[#E0E0E0] text-[#6B6B6B] hover:text-[#5B2C93] hover:bg-[#E8DCF5]"
                      onClick={() => handleShare(viewerItem)}
                    >
                      <Copy className="h-3.5 w-3.5" />
                      {t("knowledge.copyLink", "Copy Link")}
                    </Button>
                  </div>
                </div>
              </DialogHeader>

              <div className="flex-1 overflow-hidden flex flex-col" style={{ minHeight: 0 }}>
                {viewerItem.contentType === "PDF" ? (
                  <PDFViewer
                    url={contentUrl}
                    fileName={viewerItem.title}
                  />
                ) : (
                  <VideoPlayer
                    url={contentUrl}
                    title={viewerItem.title}
                  />
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
